import { Box, IconButton, TableCell, TableRow, Tooltip } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { DetailRow } from "~/components/data-table";
import { FirewatchChip } from "~/components/FirewatchRow/FirewatchChip";
import { RelativeDateCell } from "~/components/FirewatchRow/RelativeDateCell";
import { Flex } from "~/components/Flex";
import {
  AccessTime as AccessTimeIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  TimerOutlined as TimerOutlinedIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { Text, TextCaption } from "~/components/ui-library";
import { FormatDate } from "~/lib/date";
import {
  AuthorType,
  ExceptionGroupExtension,
  ExceptionReviewAction,
} from "~/operations";
import { ExceptionGroup } from "../types";
import { ReviewStatusChip } from "./ReviewStatusChip";
import { ExceptionTypeChip } from "./ExceptionTypeChip";
import { ExceptionTypeIcon } from "./ExceptionTypeIcon";
import ExceptionButtons from "~/pages/findings/Exceptions/components/ExceptionButtons";
import { ExceptionGroupTables } from "~/pages/findings/Exceptions/components/ExceptionGroupTables";
import useExceptionsGroupRow from "../hooks/useExceptionsGroupRow";
import { useScope } from "~/hooks/useScope";
import { SpacesIcon } from "~/icons";
import { truncateText } from "~/lib/truncateText";

export type ExceptionGroupRowProps = {
  exceptionGroup: ExceptionGroup;
  onReviewActionChange?: (
    value: ExceptionGroup,
    action: ExceptionReviewAction,
  ) => void;
  isOpen: boolean;
  isFocused: boolean;
  onToggle: (id: string) => void;
  isReviewLoading?: boolean;
  loadingAction?: ExceptionReviewAction;
  loadingExceptionId?: string;
  extensions: ExceptionGroupExtension[];
  refetch?: () => void;
  displayScope?: boolean;
};

export function ExceptionGroupRow({
  exceptionGroup,
  onReviewActionChange,
  isOpen,
  isFocused,
  onToggle,
  isReviewLoading = false,
  loadingAction,
  loadingExceptionId,
  extensions,
  refetch,
  displayScope,
}: ExceptionGroupRowProps) {
  const { spaceScope } = useScope();
  const {
    asset,
    exceptionGroupId,
    isAssetLoading,
    isThisExceptionLoading,
    removeExceptionsGroupDialogPayload,
    scopeTag,
  } = useExceptionsGroupRow({
    exceptionGroup,
    isReviewLoading,
    loadingExceptionId,
    isOpen,
    isFocused,
  });

  if (!spaceScope) return;

  return (
    <Fragment>
      <TableRow
        id={exceptionGroupId}
        className={"exception-group-summary-row"}
        sx={{
          background: (theme) =>
            isFocused ? theme.palette.background.lighter : "unset",
        }}
      >
        <TableCell sx={{}}>
          {exceptionGroupId}
          <Box display="flex" gap={1} mt={1}>
            <ExceptionTypeChip action={exceptionGroup.action} />
            {displayScope && (
              <FirewatchChip
                tag={{
                  __typename: "Tag",
                  key: "Space",
                  value: truncateText(spaceScope.name, 20),
                }}
              />
            )}
            {scopeTag && (
              <FirewatchChip
                tag={{
                  __typename: "Tag",
                  key: "Scope",
                  value: scopeTag,
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <ReviewStatusChip
            status={exceptionGroup.status}
            isActive={exceptionGroup.isActive}
            isRemoved={exceptionGroup.deletedAt !== null}
          />
        </TableCell>
        <RelativeDateCell date={exceptionGroup.createdAt} addSuffix />
        <RelativeDateCell date={exceptionGroup.validUntil} addSuffix />
        <TableCell align="right">
          <IconButton
            aria-label={isOpen ? "collapse query" : "expand query"}
            size="small"
            onClick={() => onToggle(exceptionGroupId)}
          >
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      {/* Accordion Content Row */}
      <DetailRow
        colSpan={5}
        open={isOpen}
        tdSx={{
          borderWidth: isOpen ? "1px" : "0",
          transition: (theme) => theme.transitions.create("border-width"),
        }}
      >
        <Flex flexDirection="column" flex={1} p={2} gap={2}>
          <Flex justifyContent="space-between">
            <Flex flexDirection="column" gap={1}>
              <Flex justifyContent="space-between">
                <Flex center gap={2}>
                  <ExceptionTypeIcon action={exceptionGroup.action} />
                  <Text bold fontSize={24}>
                    {exceptionGroup.title}
                  </Text>
                </Flex>
              </Flex>
              <Flex justifyContent="space-between">
                <Flex gap={2}>
                  <Flex center gap={1}>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    />
                    <TextCaption bold secondary>
                      {exceptionGroupId}
                    </TextCaption>
                  </Flex>
                  {displayScope && (
                    <Flex center gap={1}>
                      <SpacesIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <TextCaption bold secondary>
                        {spaceScope.name}
                      </TextCaption>
                    </Flex>
                  )}
                  {exceptionGroup.author && (
                    <Tooltip
                      title={`Created by: ${exceptionGroup.author.name}`}
                      arrow
                      placement="top"
                    >
                      <Flex center gap={1}>
                        <PersonOutlineIcon
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        />
                        {/* if author.type is SERVICE_ACCOUNT, link to the service account page */}
                        <TextCaption
                          bold
                          secondary
                          {...(exceptionGroup.author.type ===
                            AuthorType.ServiceAccount && {
                            component: RouterLink,
                            to: `/space/settings/service-accounts?${spaceScope?.params}&queryTerms=${exceptionGroup.author.name}`,
                            sx: {
                              color: "primary.light",
                            },
                          })}
                        >
                          {exceptionGroup.author.name}
                        </TextCaption>
                      </Flex>
                    </Tooltip>
                  )}
                  <Tooltip
                    title={`Created: ${FormatDate(exceptionGroup.createdAt)}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <AccessTimeIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <TextCaption bold secondary>
                        {FormatDate(exceptionGroup.createdAt)}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                  {exceptionGroup.validUntil && (
                    <Tooltip
                      title={`Valid Until: ${FormatDate(exceptionGroup.validUntil)}`}
                      arrow
                      placement="top"
                    >
                      <Flex center gap={1}>
                        <TimerOutlinedIcon
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        />
                        <TextCaption bold secondary>
                          {FormatDate(exceptionGroup.validUntil)}
                        </TextCaption>
                      </Flex>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Flex flexDirection="column" gap={1.5} alignItems="flex-end">
              <ExceptionButtons
                exceptionGroup={exceptionGroup}
                onReviewActionChange={onReviewActionChange}
                isLoading={isThisExceptionLoading}
                loadingAction={loadingAction}
                removeExceptionGroupPayload={removeExceptionsGroupDialogPayload}
                refetch={refetch}
                enabledExtension
              />

              {exceptionGroup.reviewer && (
                <Flex gap={2}>
                  <Tooltip
                    title={`Reviewed by: ${exceptionGroup.reviewer.name}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <VisibilityIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <TextCaption bold secondary>
                        {exceptionGroup.reviewer.name}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                  <Tooltip
                    title={`Reviewed: ${FormatDate(exceptionGroup.modifiedAt)}`}
                    arrow
                    placement="top"
                  >
                    <Flex center gap={1}>
                      <AccessTimeIcon
                        sx={{ fontSize: 12, color: "text.secondary" }}
                      />
                      <TextCaption bold secondary>
                        {FormatDate(exceptionGroup.modifiedAt)}
                      </TextCaption>
                    </Flex>
                  </Tooltip>
                </Flex>
              )}
            </Flex>
          </Flex>

          {exceptionGroup.justification && (
            <Text secondary>{exceptionGroup.justification}</Text>
          )}

          <ExceptionGroupTables
            assetLoading={isAssetLoading}
            exceptionGroup={exceptionGroup}
            exceptionScope="space"
            assets={asset ? [asset] : []}
            showFindingsWhenSingle
            extensions={extensions}
          />
        </Flex>
      </DetailRow>
    </Fragment>
  );
}
